import React, { useEffect, useState, useRef } from 'react'
import Input from '../../components/Input'
import Keyboard from '../../components/Keyboard'
import CollectionsSetTS from '../../components/CollectionsSet'
import tts from '../../lib/TTS.js'
import SearchScreenHeader from './SearchScreenHeader'
import SearchScreenResult from './SearchScreenResult'
import {
  FocusableComponentLayout,
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { useSearchResult } from './hooks/useSearchResult'
import cx from 'classnames'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'
import { platformTransitionStyle } from '../../../../shared/helpers.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'

const KEYBOARD_COMPONENT_FOCUS_KEY = 'KEYBOARD_COMPONENT'
const SAVE_SEARCH_QUERY_ACTION_NAME = 'SAVE_SEARCH_QUERY'

const COLLECTION_FOCUS_TRANSLATE_Y = 434
const MAX_TRANSLATE_Y = 0

const SearchScreen = () => {
  const searchQueryRedux = useAppSelector((state) => state.search.searchQuery)
  const dispatch = useAppDispatch()

  const { isSidePanelOpened, setAvoidStealFocus } = useSidePanel()
  const [translateY, setTranslateY] = useState(0)
  const searchScreenRef = useRef<HTMLDivElement>(null)
  const contentWrapperRef = useRef<HTMLDivElement>(null)

  const {
    searchQueryRef,
    collectionsReady,
    searchResultCollection,
    setSearchResult,
    setInitialSearchResults,
    setNextCarouselResult,
    error,
  } = useSearchResult()

  useEffect(() => {
    !searchQueryRedux && setInitialSearchResults()
    searchQueryRedux && setAvoidStealFocus(true)

    googleAnalytics4.sendPageViewEvent('Search')

    return () => setAvoidStealFocus(false)
  }, [])

  useEffect(() => {
    if (searchResultCollection.length === 0 && error)
      getCurrentFocusKey() !== KEYBOARD_COMPONENT_FOCUS_KEY &&
        handleFocusBackToKeyboard()
  }, [error, searchResultCollection])

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      setTranslateY((prevTranslateY) => {
        const newTranslateY = prevTranslateY - e.deltaY
        const contentWrapper = contentWrapperRef.current
        const searchScreen = searchScreenRef.current
        if (contentWrapper && searchScreen) {
          const contentHeight = contentWrapper.getBoundingClientRect().height
          const viewportHeight = searchScreen.getBoundingClientRect().height
          const minTranslateY = Math.min(0, viewportHeight - contentHeight)

          return Math.max(
            minTranslateY,
            Math.min(MAX_TRANSLATE_Y, newTranslateY)
          )
        }

        return Math.max(-5000, Math.min(MAX_TRANSLATE_Y, newTranslateY))
      })
    }

    const searchScreenElement = searchScreenRef.current
    if (searchScreenElement) {
      searchScreenElement.addEventListener('wheel', handleWheel, {
        passive: false,
      })
    }

    return () => {
      if (searchScreenElement) {
        searchScreenElement.removeEventListener('wheel', handleWheel)
      }
    }
  }, [])

  const handleSearchCleared = () => {
    setInitialSearchResults()
    setTranslateY(0)
    tts.addTextToRead('Search Cleared')
  }

  const handleLeftArrowExit = () => {
    dispatch({
      type: SAVE_SEARCH_QUERY_ACTION_NAME,
      payload: { searchQuery: '' },
    })
  }

  const handleFocusBackToKeyboard = () => {
    setFocus(KEYBOARD_COMPONENT_FOCUS_KEY)
  }

  const onArrowPress = (direction: string, props: { index: number }) => {
    // eslint-disable-next-line react/prop-types
    if (direction === 'left' && props.index === 0) {
      handleFocusBackToKeyboard()
      return false
    }
    return true
  }

  const emptyCollectionResponse =
    searchResultCollection?.length === 0 && collectionsReady

  const handleCollectionsFocus = (layout: FocusableComponentLayout) => {
    const newTranslateY = -(layout.top - COLLECTION_FOCUS_TRANSLATE_Y)
    setTranslateY(newTranslateY)
  }

  return (
    <div
      ref={searchScreenRef}
      className={cx('search-screen', {
        'search-screen--side-panel-closed': !isSidePanelOpened,
      })}
    >
      <div
        ref={contentWrapperRef}
        style={{
          transition: platformTransitionStyle(`transform 500ms ease-in-out`),
          transform: `translateY(${translateY}px)`,
          paddingBottom: '50px',
        }}
      >
        <SearchScreenHeader
          getSearchResults={setSearchResult}
          handleSearchCleared={handleSearchCleared}
          emptyCollectionResponse={emptyCollectionResponse}
          handleLeftArrowExit={handleLeftArrowExit}
          onFocus={() => setTranslateY(0)}
        >
          {(searchQuery, showCursor, onEnterPress, onArrowPress) => (
            <>
              <Input searchQuery={searchQuery} showCursor={showCursor} />
              <Keyboard
                onArrowPress={onArrowPress}
                onEnterPress={onEnterPress}
              />
            </>
          )}
        </SearchScreenHeader>
        <SearchScreenResult
          emptyCollectionResponse={emptyCollectionResponse}
          searchError={error}
          collectionsReady={collectionsReady}
          searchQuery={searchQueryRef.current}
        >
          {collectionsReady && (
            <CollectionsSetTS
              collections={searchResultCollection}
              onArrowPress={onArrowPress}
              handleInfiniteFetching={setNextCarouselResult}
              onFocus={handleCollectionsFocus}
              hasHeaderComponent={true}
              handelWheelExternally={true}
            />
          )}
        </SearchScreenResult>
      </div>
    </div>
  )
}

export default SearchScreen
